import { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { MenuList, Tooltip } from '@clickguard/design-system'
import type { HeaderPopover } from 'layout/navbar/notifications/types'
import Sun from '@untitled-ui/icons-react/build/esm/Sun'
import Moon01 from '@untitled-ui/icons-react/build/esm/Moon01'
import Monitor01 from '@untitled-ui/icons-react/build/esm/Monitor01'
import Check from '@untitled-ui/icons-react/build/esm/Check'
import {
	getStoredPreference,
	transitionTheme,
	systemTheme,
	type ThemePreference,
	type ResolvedTheme
} from 'utils/theme'
import { Users } from 'services/user'

interface ThemeMenuProps {
	headerPopover: HeaderPopover
	setHeaderPopover: (v: HeaderPopover) => void
}

const OPTIONS: { value: ThemePreference; label: string; icon: any }[] = [
	{ value: 'light', label: 'Light', icon: Sun },
	{ value: 'dark', label: 'Dark', icon: Moon01 },
	{ value: 'system', label: 'System', icon: Monitor01 }
]

export const ThemeMenu = ({
	headerPopover,
	setHeaderPopover
}: ThemeMenuProps) => {
	const open = headerPopover === 'theme'
	const setOpen = (v: boolean) => setHeaderPopover(v ? 'theme' : null)
	const containerRef = useRef<HTMLDivElement>(null)
	const buttonRef = useRef<HTMLButtonElement>(null)

	const [preference, setPreference] =
		useState<ThemePreference>(getStoredPreference)
	const [systemResolved, setSystemResolved] =
		useState<ResolvedTheme>(systemTheme)

	useEffect(() => {
		if (!open) return
		const handleClickOutside = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setOpen(false)
			}
		}
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setOpen(false)
		}
		document.addEventListener('mousedown', handleClickOutside)
		document.addEventListener('keydown', handleEscape)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			document.removeEventListener('keydown', handleEscape)
		}
	}, [open])

	// The button icon reflects the RESOLVED theme; track OS flips so it stays
	// correct while 'system' is selected.
	useEffect(() => {
		const mq = window.matchMedia('(prefers-color-scheme: dark)')
		const onChange = () => setSystemResolved(systemTheme())
		// The authenticated reader can request a preference through its parent bridge.
		const observer = new MutationObserver(() =>
			setPreference(getStoredPreference())
		)
		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ['data-theme']
		})
		mq.addEventListener('change', onChange)
		return () => {
			observer.disconnect()
			mq.removeEventListener('change', onChange)
		}
	}, [])

	const resolved = preference === 'system' ? systemResolved : preference

	const select = (value: ThemePreference) => {
		// Circle reveal originates from the center of the navbar trigger.
		const rect = buttonRef.current?.getBoundingClientRect()
		const origin = rect
			? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
			: undefined
		transitionTheme(value, {
			origin,
			onApply: () => flushSync(() => setPreference(value))
		})
		// Persist to the user's record so the choice is recorded for adoption
		// analytics. Best-effort — the UI already updated via localStorage.
		Users.updateTheme(value).catch(() => undefined)
		setOpen(false)
	}

	return (
		<div className="relative flex items-center" ref={containerRef}>
			<Tooltip content="Theme" position="bottom">
				<button
					ref={buttonRef}
					aria-label="Theme"
					aria-expanded={open}
					onClick={() => setOpen(!open)}
					className="flex items-center justify-center p-[6px] rounded-[8px] hover:bg-gray-100 transition-colors cursor-pointer"
				>
					{resolved === 'dark' ? (
						<Moon01
							width={24}
							height={24}
							className="text-[rgb(var(--cg-text-secondary))]"
						/>
					) : (
						<Sun
							width={24}
							height={24}
							className="text-[rgb(var(--cg-text-secondary))]"
						/>
					)}
				</button>
			</Tooltip>

			{open && (
				<div className="absolute right-0 top-full mt-2 z-50">
					<MenuList className="w-[200px]">
						{OPTIONS.map(({ value, label, icon: Icon }) => (
							<button
								key={value}
								onClick={() => select(value)}
								className="flex items-center gap-[8px] w-full min-h-[36px] p-[8px] rounded-[10px] hover:bg-t-step-inactive transition-colors cursor-pointer"
							>
								<span className="flex items-center justify-center shrink-0 h-[20px] w-[20px] text-t-coupon">
									<Icon width={20} height={20} />
								</span>
								<span className="flex-1 text-left text-[14px] leading-[18px] text-t-coupon truncate">
									{label}
								</span>
								{preference === value && (
									<Check
										width={16}
										height={16}
										className="shrink-0 text-[rgb(var(--cg-text-brand))]"
									/>
								)}
							</button>
						))}
					</MenuList>
				</div>
			)}
		</div>
	)
}
